/**
 * useOAuthPopup - Hook for handling OAuth authentication in a popup window
 * Part of PROJ-025: Azure CIAM & Google OAuth Integration
 *
 * Benefits of popup over redirect:
 * - User stays on the app while authenticating
 * - Better UX - no full page navigation
 * - App state is preserved
 */

import { useCallback, useEffect, useRef } from 'react'

interface OAuthPopupOptions {
  /** URL to open in the popup */
  url: string
  /** Window name for the popup */
  name?: string
  /** Popup window width */
  width?: number
  /** Popup window height */
  height?: number
  /** Callback when authentication succeeds */
  onSuccess?: () => void
  /** Callback when authentication fails */
  onError?: (error: string) => void
  /** Callback when popup is closed without completing auth */
  onClose?: () => void
}

interface OAuthMessage {
  type: 'oauth_success' | 'oauth_error' | 'oauth_close'
  provider?: string
  error?: string
}

/**
 * Opens OAuth login in a centered popup window
 */
export function openOAuthPopup(options: OAuthPopupOptions): Window | null {
  const {
    url,
    name = 'oauth-popup',
    width = 500,
    height = 650,
  } = options

  // Calculate center position
  const left = Math.max(0, (window.screen.width - width) / 2 + window.screenX)
  const top = Math.max(0, (window.screen.height - height) / 2 + window.screenY)

  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'toolbar=no',
    'menubar=no',
    'scrollbars=yes',
    'resizable=yes',
    'status=no',
  ].join(',')

  const popup = window.open(url, name, features)

  if (popup) {
    popup.focus()
  }

  return popup
}

/**
 * Hook for managing OAuth popup authentication
 */
export function useOAuthPopup() {
  const popupRef = useRef<Window | null>(null)
  const checkIntervalRef = useRef<number | null>(null)

  // Cleanup function
  const cleanup = useCallback(() => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current)
      checkIntervalRef.current = null
    }
    try {
      // COOP (Cross-Origin-Opener-Policy) may block access to window.closed
      // Wrap in try-catch to handle SecurityError from Azure CIAM
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close()
      }
    } catch (e) {
      // COOP blocked access - popup is likely from a different origin
      // Just clear our reference, the popup will handle itself
      console.debug('COOP blocked popup access during cleanup')
    }
    popupRef.current = null
  }, [])

  // Listen for messages from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent<OAuthMessage>) => {
      // Only accept messages from our origin
      if (event.origin !== window.location.origin) return

      const { type } = event.data || {}

      if (type === 'oauth_success') {
        cleanup()
        // Force page reload to update auth state
        window.location.reload()
      } else if (type === 'oauth_error') {
        cleanup()
        console.error('OAuth error:', event.data.error)
      } else if (type === 'oauth_close') {
        cleanup()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [cleanup])

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  const openPopup = useCallback((url: string, provider: string) => {
    // Store provider hint
    sessionStorage.setItem('oauth_provider', provider)
    sessionStorage.setItem('oauth_popup', 'true')

    // Open popup
    popupRef.current = openOAuthPopup({
      url,
      name: `${provider}-oauth`,
      width: 500,
      height: 650,
    })

    if (!popupRef.current) {
      // Popup was blocked, fall back to redirect
      console.warn('Popup blocked, falling back to redirect')
      sessionStorage.removeItem('oauth_popup')
      window.location.href = url
      return
    }

    // Monitor popup status (in case it's closed without completing)
    checkIntervalRef.current = window.setInterval(() => {
      try {
        // COOP (Cross-Origin-Opener-Policy) may block access to window.closed
        // This happens when the popup navigates to Azure CIAM (different origin)
        if (popupRef.current?.closed) {
          cleanup()
        }
      } catch (e) {
        // COOP blocked access - rely on postMessage from AuthCallback instead
        // Don't cleanup here, wait for the message
        console.debug('COOP blocked popup closed check - relying on postMessage')
      }
    }, 500)
  }, [cleanup])

  return { openPopup, cleanup }
}

/**
 * Check if current window is an OAuth popup
 */
export function isOAuthPopup(): boolean {
  return sessionStorage.getItem('oauth_popup') === 'true' && window.opener !== null
}

/**
 * Send message to opener window and close popup
 */
export function notifyOpenerAndClose(type: OAuthMessage['type'], error?: string) {
  if (window.opener && !window.opener.closed) {
    window.opener.postMessage({ type, error }, window.location.origin)
  }
  // Clear popup flag
  sessionStorage.removeItem('oauth_popup')
  // Close the popup window
  window.close()
}

export default useOAuthPopup
